import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import AuthPageShell from '../components/AuthPageShell';
import FullScreenSpinner from '../components/FullScreenSpinner';
import { useAuth } from '../contexts/AuthContext';
import { getNextAuthRoute } from '../utils/authFlow';

export default function MfaSetup() {
  const {
    beginMfaEnrollment,
    completeMfaEnrollment,
    isAuthenticated,
    isEmailVerified,
    isLoading,
    isMfaChallengeRequired,
    isMfaEnrolled,
    logout,
    mfaEnrollment,
    user,
  } = useAuth();
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nextRoute = getNextAuthRoute({
    isAuthenticated,
    isEmailVerified,
    isMfaChallengeRequired,
    isMfaEnrolled,
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await completeMfaEnrollment(verificationCode);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete multi-factor setup.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateSecret = async () => {
    setError('');
    setVerificationCode('');
    setIsPreparing(true);

    try {
      await beginMfaEnrollment();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh authenticator setup.');
    } finally {
      setIsPreparing(false);
    }
  };

  const handleLogout = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      await logout();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to logout.');
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  if (nextRoute !== '/mfa-setup') {
    return <Navigate to={nextRoute} replace />;
  }

  return (
    <AuthPageShell
      title="Set Up Multi-Factor Authentication"
      description="Multi-factor authentication is required before you can use the dashboard."
      maxWidthClassName="max-w-lg"
    >
      <div className="space-y-6">
        <div className="rounded-md bg-blue-50 dark:bg-blue-950/40 p-4 text-sm text-blue-900 dark:text-blue-200">
          <p className="font-medium mb-1">Signed in as</p>
          <p className="break-all">{user?.email ?? 'Unknown email'}</p>
        </div>
        {error ? (
          <div className="rounded-md bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        ) : null}
        {!mfaEnrollment ? (
          <div className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-sm text-gray-600 dark:text-gray-300">
            {isPreparing
              ? 'Preparing your authenticator setup...'
              : 'Generate a setup secret to link this account with your authenticator app.'}
          </div>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-[240px,1fr] items-start">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 flex items-center justify-center">
                {mfaEnrollment.qrCodeDataUrl ? (
                  <img
                    src={mfaEnrollment.qrCodeDataUrl}
                    alt="Authenticator QR code"
                    className="h-56 w-56"
                  />
                ) : (
                  <div className="text-center text-sm text-gray-600 dark:text-gray-300 px-4">
                    QR preview unavailable.
                    <br />
                    Use the manual setup key instead.
                  </div>
                )}
              </div>
              <div className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
                <p>Scan this QR code with Google Authenticator, 1Password, Microsoft Authenticator, or another TOTP app.</p>
                <div>
                  <p className="font-medium text-gray-800 dark:text-white mb-1">Manual setup key</p>
                  <p className="font-mono break-all rounded-md bg-gray-100 dark:bg-gray-900 p-3 text-gray-900 dark:text-gray-100">
                    {mfaEnrollment.secretKey}
                  </p>
                </div>
                {!mfaEnrollment.qrCodeDataUrl ? (
                  <div className="rounded-md bg-yellow-50 dark:bg-yellow-950/30 p-3 text-sm text-yellow-800 dark:text-yellow-300">
                    Your browser could not render the QR image. Manual setup still works: add a TOTP account in your authenticator app and enter the setup key above.
                  </div>
                ) : null}
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <p className="font-medium text-gray-800 dark:text-white">Issuer</p>
                    <p>{mfaEnrollment.issuer}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 dark:text-white">Account</p>
                    <p className="break-all">{mfaEnrollment.accountName}</p>
                  </div>
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="verification-code"
                  className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2"
                >
                  Authenticator code
                </label>
                <input
                  type="text"
                  id="verification-code"
                  value={verificationCode}
                  onChange={(event) => setVerificationCode(event.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white transition-colors duration-200"
                  placeholder="Enter the 6-digit code"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={isSubmitting || isPreparing}
              >
                {isSubmitting ? 'Verifying...' : 'Finish setup'}
              </button>
            </form>
          </>
        )}
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={handleGenerateSecret}
            className="w-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 py-2 px-4 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isPreparing || isSubmitting}
          >
            {mfaEnrollment ? 'Generate a new secret' : 'Generate setup secret'}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full text-sm text-gray-600 dark:text-gray-300 hover:underline disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isSubmitting}
          >
            Logout
          </button>
        </div>
      </div>
    </AuthPageShell>
  );
}
